/**
 * Performance utility functions for optimization and monitoring
 */

/**
 * Measures the execution time of an operation and logs it
 */
export const measureRenderTime = <T>(
  operationNameOrOperation: string | (() => T),
  operationOrName?: (() => T) | string
): T => {
  let operation: () => T;
  let operationName: string;
  
  // Handle both parameter orders for compatibility
  if (typeof operationNameOrOperation === 'string') {
    operationName = operationNameOrOperation;
    operation = operationOrName as () => T;
  } else {
    operation = operationNameOrOperation;
    operationName = (operationOrName as string) || 'Operation';
  }
  
  const startTime = performance.now();
  const result = operation();
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  console.log(`${operationName} took ${duration.toFixed(2)}ms`);
  return result;
};

/**
 * Debounce function that delays execution until after delay milliseconds
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Throttle function that limits execution to once per interval
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  interval: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= interval) {
      lastCall = now;
      func(...args);
    }
  };
};

/**
 * Memoization function for expensive computations
 */
export const memoizeExpensive = <T extends (...args: any[]) => any>(
  func: T
): T => {
  const cache = new Map<any, ReturnType<T>>();
  
  return ((...args: Parameters<T>): ReturnType<T> => {
    // Use the first argument as the cache key for object reference-based caching
    // This matches the test expectation that different object references should not be cached together
    const key = args.length === 1 ? args[0] : JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    
    const result = func(...args);
    cache.set(key, result);
    return result;
  }) as T;
};

/**
 * Track web vitals for performance monitoring
 */
export const trackWebVitals = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    // Track Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // Track Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          console.log('LCP:', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Track First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            console.log('FID:', entry.processingStart - entry.startTime);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Track Cumulative Layout Shift (CLS)
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!(entry as any).hadRecentInput) {
              console.log('CLS:', (entry as any).value);
            }
          });
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (error) {
        console.warn('Performance monitoring not supported:', error);
      }
    }
  }
};

/**
 * Log memory usage for development monitoring
 */
export const logMemoryUsage = () => {
  if (typeof window !== 'undefined' && 'performance' in window && (performance as any).memory) {
    const memory = (performance as any).memory;
    console.log('Memory Usage:', {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + ' MB',
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + ' MB',
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
    });
  }
};